/**
 * Decision Workspace — 决策工作台
 *
 * 完整决策链路: Intent → Candidates → Constraints → Ranking → Risk → Explainability
 *
 * 设计风格与现有项目一致: 暗夜蓝 + 萤火暖金,
 * 内联样式 + CSS 变量, antd 组件做兜底
 *
 * [SSE增强] 底部实时思考日志面板:
 *   - 模拟 SSE 推送 Agent 推理过程
 *   - ThinkingLog 组件以 Timeline 展示
 *   - 展示"为什么选择XX"的推理依据
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Tag, message, Badge, Space, Tooltip, Divider } from 'antd';
import {
  BulbOutlined,
  ReloadOutlined,
  ClearOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { decisionAPI } from '../api/decision';
import { useSSE } from '../hooks/useSSE';
import ThinkingLog from '../components/ThinkingLog';
import StreamOutput from '../components/StreamOutput';

// ─── Mock SSE 模拟器 ────────────────────────────────────────────────────────
// 当无真实后端 SSE 端点时，用 setInterval 模拟事件推送
const useMockSSE = (active, onEvent) => {
  const timerRef = useRef(null);
  const stepRef = useRef(0);

  useEffect(() => {
    if (!active) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const mockEvents = createMockThinkingEvents();
    stepRef.current = 0;

    timerRef.current = setInterval(() => {
      if (stepRef.current >= mockEvents.length) {
        clearInterval(timerRef.current);
        return;
      }
      const evt = mockEvents[stepRef.current];
      onEvent(evt);
      stepRef.current += 1;
    }, 1200 + Math.random() * 800); // 1.2-2s 随机间隔

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, onEvent]);
};

/**
 * 生成 mock 思考流水线数据
 * 模拟: 意图解析 → 约束推理 → 多 Agent 辩论 → 最终仲裁 → Done
 */
function createMockThinkingEvents() {
  return [
    {
      type: 'thinking',
      data: {
        agent: 'Intent Parser',
        reasoning: '解析用户输入「周六约会 预算500 日料」→ 提取关键约束: scenario=romantic_date, budget=500, cuisine=japanese, mood=scenic_quiet',
        summary: '意图识别置信度 95%',
        score: 95,
        confidence: 0.95,
        refData: ['scenario:romantic_date', 'budget<=500'],
      },
    },
    {
      type: 'thinking',
      data: {
        agent: 'Constraint Engine',
        reasoning: '筛选候选池 284 家日料餐厅 → 预算过滤移除 ¥600+ 选项 → 剩余 31 家 → 步行可达性约束移除 5km+ → 剩余 8 家。核心约束: 夜景视野 + 安静氛围 + 评分>4.5',
        summary: '31 → 8 家 (约束淘汰率 74%)',
        score: 82,
        confidence: 0.88,
        refData: ['distance<3km', 'noise<35dB', 'rating>4.5'],
      },
    },
    {
      type: 'arbitration',
      data: {
        agent: 'Debate Moderator',
        reasoning: 'Agent「体验派」主张选择花见居酒屋 (氛围轻松, 价格亲民) 与 Agent「品质派」主张选择星空日料 (景观顶级, 品质最优) 进入帕累托最优博弈。启动仲裁协议 v2.1...',
        summary: '进入 Agent 辩论阶段',
        refData: ['agent_experience', 'agent_quality', 'pareto_optimal'],
      },
    },
    {
      type: 'thinking',
      data: {
        agent: 'Quality Agent',
        reasoning: '星空日料 · 外滩店综合评估:\n• 夜景视野: 12楼露台正对江景 — 评分 +15\n• 静谧指数: 和室包间, 噪声 <30dB — 评分 +12\n• 口感评分: 4.9/5.0 — 评分 +18\n• 性价比: ¥380/人 vs 预算 ¥500 — 评分 +10\n综合加权: 94/100 — 满足全部硬约束',
        summary: '推荐 星空日料·外滩店 (score=94)',
        score: 94,
        confidence: 0.92,
        refData: ['view:river_12f', 'noise:28dB', 'rating:4.9'],
      },
    },
    {
      type: 'thinking',
      data: {
        agent: 'Experience Agent',
        reasoning: '花见居酒屋评估: 氛围轻松活泼适合情侣互动, 价格 ¥280 节省预算。但距离 3.1km 略超偏好, 且无夜景。建议作为备选 #3。',
        summary: '备选评分: 花见=82分',
        score: 82,
        confidence: 0.85,
        refData: ['distance:3.1km', 'atmosphere:lively'],
      },
    },
    {
      type: 'arbitration',
      data: {
        agent: 'Arbitration Solver',
        reasoning: '仲裁完成。根据帕累托最优解和用户历史偏好 (偏好安静+品质>社交氛围), 选定「星空日料·外滩店」为最优解。核心依据: (1) 唯一同时满足夜景+安静+高评分; (2) 用户过往 3 次约会均选择高景观位; (3) 预算 ¥380 仅消费 76%，预留容错空间。',
        summary: '决策收敛 — 92% 置信度',
        score: 92,
        confidence: 0.92,
        refData: ['match:user_history_3x', 'budget_safety_margin:24%'],
      },
    },
    {
      type: 'done',
      data: {
        summary: '决策完成 — 共 6 步推理, 涉及 3 个 Agent 协作',
        finalChoice: '星空日料 · 外滩店',
        totalSteps: 6,
        agents: ['Intent Parser', 'Constraint Engine', 'Quality Agent', 'Experience Agent', 'Arbitration Solver'],
      },
    },
  ];
}

const DECISION_STEPS = [
  { key: 'intent', title: '意图解析', icon: '🎯' },
  { key: 'candidates', title: '候选生成', icon: '📋' },
  { key: 'constraints', title: '约束过滤', icon: '⚡' },
  { key: 'ranking', title: '决策评分', icon: '📊' },
  { key: 'risk', title: '风险评估', icon: '🛡️' },
  { key: 'explain', title: '可解释性', icon: '📝' },
];

// ─── 潜例题示例 ───
const PLACEHOLDER_EXAMPLES = [
  '周六晚上和女朋友约会，预算500元，想在市中心找一家氛围好的日料餐厅...',
  '下周三去北京出差两天，需要安排机票、酒店、商务晚宴，预算3000元',
  '周日带5岁孩子去公园玩耍，预算200元，需要午餐和下午茶安排',
];

export default function DecisionWorkspace() {
  const [currentStep, setCurrentStep] = useState(0);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('candidates');

  // ─── SSE 思考日志状态 ───
  const [isSSEActive, setIsSSEActive] = useState(false);
  const [thinkingLogs, setThinkingLogs] = useState([]);
  const [isStreamConnected, setIsStreamConnected] = useState(false);
  const [streamComplete, setStreamComplete] = useState(false);
  const [agentSummary, setAgentSummary] = useState('');

  // Mock SSE 事件回调
  const handleMockEvent = useCallback((evt) => {
    setThinkingLogs((prev) => [...prev, { ...evt, time: Date.now() }]);
    if (evt.type === 'done') {
      setStreamComplete(true);
      setIsStreamConnected(false);
      setIsSSEActive(false);
      setAgentSummary(evt.data?.summary || 'Agent 推理完成');
    }
  }, []);

  // 启动 Mock SSE 模拟器
  useMockSSE(isSSEActive, handleMockEvent);

  // 执行决策
  const handleDecide = useCallback(async () => {
    if (!input.trim()) {
      message.warning('请输入您的需求');
      return;
    }
    setLoading(true);
    setResult(null);
    setCurrentStep(0);

    // 清空旧日志并启动 SSE 模拟
    setThinkingLogs([]);
    setStreamComplete(false);
    setAgentSummary('');
    setIsStreamConnected(true);
    setIsSSEActive(true);

    try {
      // 模拟分步动画
      const stepTimer = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev >= 4) { clearInterval(stepTimer); return prev; }
          return prev + 1;
        });
      }, 400);

      // 调用 API (兜底 mock)
      let data;
      try {
        data = await decisionAPI.decide(input, {});
      } catch {
        data = await decisionAPI.mockDecide(input);
      }

      clearInterval(stepTimer);
      setCurrentStep(5);
      setResult(data);
    } catch (err) {
      message.error('决策引擎调用失败');
    } finally {
      setLoading(false);
    }
  }, [input]);

  return (
    <div style={styles.pageShell}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseSoft {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
        @keyframes streamBlink {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0; }
        }
      `}</style>

      {/* ── Header ── */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <span style={styles.titleIcon}>🧠</span>
            Decision Workspace
          </h1>
          <p style={styles.subtitle}>
            Intent → Decision → Ranking → Simulation → Explainability
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          disabled={!result}
          style={styles.executeBtn}
          onClick={() => message.success('进入执行中心...')}
        >
          执行方案 <span style={{ marginLeft: 6 }}>→</span>
        </Button>
      </header>

      {/* ── Steps Progress ── */}
      <div style={styles.stepsWrapper}>
        {DECISION_STEPS.map((step, idx) => {
          const isActive = idx === currentStep && loading;
          const isDone = idx < currentStep || (idx === 5 && currentStep === 5);
          return (
            <div key={step.key} style={styles.stepItem}>
              <div style={{
                ...styles.stepDot,
                background: isDone ? '#34d399' : isActive ? 'var(--accent)' : 'var(--glass-sm)',
                border: `2px solid ${isDone ? '#34d399' : isActive ? 'var(--accent)' : 'var(--border-default)'}`,
                boxShadow: isActive ? '0 0 16px var(--accent-glow)' : 'none',
                animation: isActive ? 'pulseSoft 1.2s ease-in-out infinite' : 'none',
              }}>
                {isDone ? '✓' : step.icon}
              </div>
              <span style={{
                ...styles.stepLabel,
                color: isDone ? '#34d399' : isActive ? 'var(--accent)' : 'var(--text-muted)',
              }}>
                {step.title}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Input ── */}
      <div style={styles.inputCard}>
        <div style={styles.inputHeader}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
            Universal Intent — 描述你的目标
          </span>
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={PLACEHOLDER_EXAMPLES[0]}
          rows={3}
          style={styles.textarea}
        />
        <div style={styles.inputFooter}>
          <div style={styles.exampleTags}>
            {PLACEHOLDER_EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => setInput(ex)}
                style={styles.exampleTag}
              >
                {ex.slice(0, 20)}...
              </button>
            ))}
          </div>
          <Button
            type="primary"
            size="large"
            onClick={handleDecide}
            loading={loading}
            style={styles.decideBtn}
          >
            {loading ? '决策中…' : '开始决策'}
            {!loading && <span style={{ fontSize: 12 }}>→</span>}
          </Button>
        </div>
      </div>

      {/* ── Result Area ── */}
      {result ? (
        <div style={styles.resultArea}>
          {/* Tab 切换 */}
          <div style={styles.tabBar}>
            {[
              { key: 'candidates', label: '候选方案', icon: '📋' },
              { key: 'ranking', label: '评分排序', icon: '📊' },
              { key: 'risk', label: '风险评估', icon: '🛡️' },
              { key: 'explain', label: '决策解释', icon: '📝' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  ...styles.tabBtn,
                  ...(activeTab === tab.key ? styles.tabBtnActive : {}),
                }}
              >
                <span style={{ fontSize: 14 }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Candidates Tab */}
          {activeTab === 'candidates' && (
            <div style={styles.tabContent}>
              <div style={styles.cardList}>
                {result.candidates?.map((c, i) => (
                  <div
                    key={c.id}
                    style={{
                      ...styles.candidateCard,
                      borderColor: i === 0 ? 'var(--accent)' : 'var(--border-default)',
                      background: i === 0 ? 'var(--accent-soft)' : 'var(--surface-1)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{
                            fontSize: 18, fontWeight: 700,
                            color: i === 0 ? 'var(--accent)' : 'var(--text-muted)',
                          }}>
                            #{i + 1}
                          </span>
                          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {c.name}
                          </span>
                          <Tag color="blue" style={{ fontSize: 11 }}>{c.type}</Tag>
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                          <span>⭐ {c.rating}</span>
                          <span>📍 {c.distance}</span>
                          <span>💡 {c.highlight}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>
                          {c.score}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Decision Score</div>
                      </div>
                    </div>
                    {/* Constraint check */}
                    <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {result.constraints?.map((con, j) => (
                        <span key={j} style={{
                          fontSize: 11,
                          padding: '3px 10px',
                          borderRadius: 'var(--radius-pill)',
                          background: con.passed ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)',
                          color: con.passed ? '#34d399' : '#f87171',
                          border: `1px solid ${con.passed ? 'rgba(52,211,153,0.2)' : 'rgba(248,113,113,0.2)'}`,
                        }}>
                          {con.passed ? '✓' : '✗'} {con.constraint}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ranking Tab */}
          {activeTab === 'ranking' && (
            <div style={styles.tabContent}>
              {result.candidates?.map((c, i) => (
                <div key={c.id} style={styles.rankItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500 }}>
                      #{i + 1} {c.name}
                    </span>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        置信 {Math.round((c.confidence || 0) * 100)}%
                      </span>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: c.riskLevel === 'LOW' ? 'rgba(52,211,153,0.1)' : 'rgba(251,191,36,0.1)',
                        color: c.riskLevel === 'LOW' ? '#34d399' : '#fbbf24',
                      }}>
                        Risk {c.riskLevel}
                      </span>
                    </div>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${c.score}%`,
                      background: i === 0 ? 'linear-gradient(90deg, var(--accent), #34d399)' : 'var(--accent-secondary)',
                    }} />
                    <span style={styles.progressText}>{c.score}/100</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Risk Tab */}
          {activeTab === 'risk' && (
            <div style={styles.tabContent}>
              <div style={styles.riskHeader}>
                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                  综合风险评估
                </span>
                <span style={{
                  padding: '4px 14px', borderRadius: 'var(--radius-pill)',
                  background: 'rgba(52,211,153,0.1)', color: '#34d399',
                  fontSize: 13, fontWeight: 600,
                }}>
                  {result.riskAssessment?.overall || 'LOW'}
                </span>
              </div>
              {result.riskAssessment?.items?.map((item, i) => (
                <div key={i} style={styles.riskItem}>
                  <span style={{ fontSize: 16 }}>
                    {item.level === 'LOW' ? '🟢' : item.level === 'MEDIUM' ? '🟡' : '🔴'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{item.type}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Explain Tab */}
          {activeTab === 'explain' && (
            <div style={styles.tabContent}>
              <h4 style={{ fontSize: 14, color: 'var(--accent)', marginBottom: 16 }}>
                <span style={{ marginRight: 8 }}>📝</span>
                为什么选择 #{result.candidates?.[0]?.name}？
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { icon: '✓', text: '预算匹配: 人均 ¥380，预算 ¥500 内', color: '#34d399' },
                  { icon: '✓', text: '景观适配: 12楼正对露台，夜景视野开阔', color: '#34d399' },
                  { icon: '✓', text: '氛围匹配: 静谧和室，情侣首选', color: '#34d399' },
                  { icon: '✓', text: '口碑优秀: 评分 4.8，92% 正面评价', color: '#34d399' },
                  { icon: '✓', text: '历史偏好: 命中用户日料偏好记录', color: '#34d399' },
                  { icon: '△', text: '步行 12min 至江边 — 略超出偏好范围', color: '#fbbf24' },
                ].map((item, i) => (
                  <div key={i} style={styles.explainItem}>
                    <span style={{ color: item.color, fontSize: 14, marginRight: 10 }}>{item.icon}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.text}</span>
                  </div>
                ))}
              </div>
              <div style={styles.confidenceBox}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Confidence</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>92%</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        !loading && (
          <div style={styles.emptyState}>
            <span style={{ fontSize: 40 }}>🎯</span>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginTop: 12 }}>
              输入你的目标，开始智能决策
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              SIMON 会为你生成候选方案、评分排序、风险评估和决策解释
            </p>
          </div>
        )
      )}

      {/* ── 底部 SSE 思考日志面板 ── */}
      <div style={styles.thinkingLogPanel}>
        <div style={styles.thinkingLogHeader}>
          <Space size={10}>
            <ThunderboltOutlined style={{ color: 'var(--accent)', fontSize: 15 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              实时推理引擎
            </span>
            <Badge
              status={isSSEActive ? 'processing' : streamComplete ? 'success' : 'default'}
              text={
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {isSSEActive ? '推理中...' : streamComplete ? '已完成' : '待机'}
                </span>
              }
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {thinkingLogs.length > 0 && `${thinkingLogs.length} 步`}
            </span>
          </Space>
          <Space size={6}>
            <Tooltip title="重新播放">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                disabled={isSSEActive || thinkingLogs.length === 0}
                onClick={() => {
                  setThinkingLogs([]);
                  setStreamComplete(false);
                  setAgentSummary('');
                  setIsStreamConnected(true);
                  setIsSSEActive(true);
                }}
                style={styles.thinkingLogBtn}
              />
            </Tooltip>
            <Tooltip title="清空日志">
              <Button
                type="text"
                size="small"
                icon={<ClearOutlined />}
                disabled={thinkingLogs.length === 0}
                onClick={() => {
                  setThinkingLogs([]);
                  setStreamComplete(false);
                  setAgentSummary('');
                }}
                style={styles.thinkingLogBtn}
              />
            </Tooltip>
          </Space>
        </div>

        {/* StreamOutput: 流式标题显示 */}
        {agentSummary && (
          <div style={styles.summaryBox}>
            <StreamOutput content={agentSummary} speed={25} prefixIcon="✓" />
          </div>
        )}

        {/* ThinkingLog 主体 */}
        <ThinkingLog logs={thinkingLogs} maxHeight={380} />
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  pageShell: {
    minHeight: '100vh',
    /* 透出 #root::before 的森林背景（登录页原图） */
    background: 'transparent',
    padding: '32px 36px 80px',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  title: {
    fontSize: 26,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  titleIcon: { fontSize: 28 },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginTop: 6,
    letterSpacing: '0.04em',
  },
  executeBtn: {
    background: 'linear-gradient(135deg, var(--accent) 0%, #C4A96A 100%)',
    border: 'none',
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    color: 'var(--night-950)',
    boxShadow: '0 4px 16px rgba(232,210,138,0.2)',
  },
  stepsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    padding: '20px 24px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    marginBottom: 24,
    animation: 'fadeUp var(--duration-slow) var(--ease) 100ms both',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  stepDot: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    transition: 'all var(--duration-normal) var(--ease)',
  },
  stepLabel: {
    fontSize: 11,
    fontWeight: 500,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  inputCard: {
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    padding: '24px',
    marginBottom: 24,
    animation: 'fadeUp var(--duration-slow) var(--ease) 200ms both',
  },
  inputHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  textarea: {
    width: '100%',
    background: 'var(--glass-sm)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    padding: '14px 16px',
    color: 'var(--text-primary)',
    fontSize: 14,
    lineHeight: 1.6,
    resize: 'none',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color var(--duration-fast) var(--ease)',
  },
  inputFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
  },
  exampleTags: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    flex: 1,
  },
  exampleTag: {
    fontSize: 11,
    padding: '5px 12px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--glass-sm)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease)',
    fontFamily: 'inherit',
  },
  decideBtn: {
    background: 'linear-gradient(135deg, var(--accent) 0%, #C4A96A 100%)',
    border: 'none',
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    color: 'var(--night-950)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 24px',
  },
  resultArea: {
    animation: 'fadeUp var(--duration-slow) var(--ease) both',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 20,
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 20px',
    borderRadius: 10,
    border: '1px solid var(--border-subtle)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--ease)',
    fontFamily: 'inherit',
  },
  tabBtnActive: {
    background: 'var(--accent-soft)',
    borderColor: 'var(--accent)',
    color: 'var(--accent)',
  },
  tabContent: {
    minHeight: 300,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  candidateCard: {
    padding: '20px 22px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--surface-1)',
    transition: 'all var(--duration-normal) var(--ease)',
  },
  rankItem: {
    padding: '16px 20px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    marginBottom: 12,
  },
  progressBar: {
    position: 'relative',
    height: 8,
    borderRadius: 4,
    background: 'var(--glass-sm)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width var(--duration-slow) var(--ease)',
  },
  progressText: {
    position: 'absolute',
    right: 0,
    top: -20,
    fontSize: 12,
    color: 'var(--text-muted)',
    fontWeight: 600,
  },
  riskHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    marginBottom: 16,
  },
  riskItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    marginBottom: 8,
  },
  explainItem: {
    padding: '12px 16px',
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    display: 'flex',
    alignItems: 'flex-start',
  },
  confidenceBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    marginTop: 20,
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(232,210,138,0.2)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
  },
  thinkingLogPanel: {
    marginTop: 28,
    background: 'var(--surface-1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    overflow: 'hidden',
    animation: 'fadeUp var(--duration-slow) var(--ease) 300ms both',
  },
  thinkingLogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'rgba(0,0,0,0.15)',
  },
  thinkingLogBtn: {
    color: 'var(--text-muted)',
  },
  summaryBox: {
    padding: '10px 20px',
    background: 'rgba(52,211,153,0.04)',
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: 13,
    color: '#34d399',
  },
};
